import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  Gallery,
  GalleryRef,
  ImageItem,
  ImageSize,
  ThumbnailsPosition,
} from 'ng-gallery';
import { Lightbox } from 'ng-gallery/lightbox';
import { TemplateImageEditComponent } from '../template-image-edit/template-image-edit.component';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { CommonConfirmationDialog } from '../../../../shared/models/user-preferences.model';
import { catchError, of } from 'rxjs';
import { ToastService } from '../../../../shared/services/toast/toast.service';
import { GlobalService } from '../../../../shared/services/global/global-service';
import { Constants, UploadOperation } from '../../../../shared/constants/common';
import { UserPreferencesService } from '../../../../shared/services/user-preferences/user-preferences.service';
import { UploadMediaRequest } from '../../../../shared/models/template.model';
import { UserPreferencesData } from '../../../../shared/models/user-preferences.model';
import { FilePreviewDialogComponent } from '../file-preview-dialog/file-preview-dialog.component';

interface MediaFile {
  url: string;
  data: string;
  caption?: string;
}

interface ItemInfo {
  formattedItemCode: string;
  itemCode: string;
  description?: string;
}

@Component({
  selector: 'app-media-file-list',
  templateUrl: './media-file-list.component.html',
  styleUrl: './media-file-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  // eslint-disable-next-line @angular-eslint/prefer-standalone -- pre-existing NgModule-declared component; standalone migration is tracked separately (COL-37/COL-36), out of scope here
  standalone: false,
})
export class MediaFileListComponent implements OnInit {
  dialogRef = inject<MatDialogRef<MediaFileListComponent>>(MatDialogRef);
  data = inject<{
    itemInfo: ItemInfo;
    media: MediaFile[];
    recordId: number;
    moduleName: string;
    mediaSection: string;
    isDisabled: boolean;
    sectionNo: number | undefined;
}>(MAT_DIALOG_DATA);
  private globalService = inject(GlobalService);
  private userPreferencesService = inject(UserPreferencesService);
  gallery = inject(Gallery);
  lightbox = inject(Lightbox);
  private dialog = inject(MatDialog);
  private toastService = inject(ToastService);

  userPreferencesData: UserPreferencesData;

  constructor() {
    this.previewImageConfig();
  }
  loadedImages: Record<string, boolean> = {};
  lightboxRef: GalleryRef;

  async ngOnInit(): Promise<void> {
    this.userPreferencesData = this.userPreferencesService.getUserPreferencesData();
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  setDefaultImage(event: Event, defaultImagePath = 'assets/images/dummy-checklist-que-image.png'): void {
      (event.target as HTMLImageElement).src = defaultImagePath;
  }

  // Check if the image is loaded
  isImageLoaded(media): boolean {
    return !!this.loadedImages[media.url];
  }

  // Mark image as loaded
  markImageAsLoaded(media): void {
    this.loadedImages[media.url] = true;
  }

  getFileUrl(media: MediaFile): string {
    switch ((media.caption ?? '').split('.').pop()?.toLowerCase() ?? '') {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        return media.url;
      case 'xls':
      case 'xlsx':
      case 'csv':
        return 'assets/icons/excel.svg';
      case 'pdf':
        return 'assets/icons/download_pdf.svg';
      default:
        return 'assets/icons/fir-icon.svg';
    }
  }

  /** Only actual image files can go through the Lightbox preview or the Pintura editor —
   * everything else must fall back to a plain download (relevant now that `MEDIA_ANS` fields
   * accept any file type). */
  isImageFile(media: MediaFile): boolean {
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(
      (media.caption ?? '').split('.').pop()?.toLowerCase() ?? ''
    );
  }

  onFileTileClick(media: MediaFile, index: number): void {
    if (this.isImageFile(media)) {
      this.previewImages(index);
    } else {
      this.openFilePreview(media);
    }
  }

  /** Opens as a real MatDialog rather than an inline overlay — this component is itself a
   * MatDialog, so an inline `position: fixed` block here got trapped inside its stacking
   * context and could be rendered over by any dialog opened afterward elsewhere in the app. */
  openFilePreview(media: MediaFile): void {
    this.dialog.open(FilePreviewDialogComponent, {
      maxWidth: '1200px',
      width: '90%',
      panelClass: 'cmn-dialog',
      data: { media },
    });
  }

  getImageNameByImageUrl(url): string {
    if (!url) return '';
    try {
      const { pathname } = new URL(url);
      return decodeURIComponent(pathname).split('/').pop() || '';
    } catch {
      return '';
    }
  }

  previewImageConfig() {
    this.lightboxRef = this.gallery.ref('lightbox');

    this.lightboxRef.setConfig({
      imageSize: ImageSize.Contain,
      thumbPosition: ThumbnailsPosition.Bottom,
      thumbs: true,
      counter: true,
    });
  }

  previewImages(index: number) {
    const imageItems = this.data.media?.map((item) => new ImageItem({ src: item.url, thumb: item.url }));
    this.lightboxRef.load(imageItems);
    this.lightbox.open(index);
  }

  openPinturaEditImageModal(media: MediaFile): void {
    const dialogRef = this.dialog.open(TemplateImageEditComponent, {
      maxWidth: '1920px',
      width: '90%',
      height: '95%',
      data: { 
        image: media.url, 
        imageKey: media.data, 
        recordId: this.data.recordId, 
        moduleName: this.data.moduleName, 
        mediaSection: this.data.mediaSection,
        itemCode: this.data.itemInfo.formattedItemCode,
        sectionNo: this.data.sectionNo
      },
      panelClass: 'cmn-dialog',
    });

    dialogRef.afterClosed().subscribe((response) => {
      if (response.success) {
        media.data = response.mediaKey;
        media.url = response.imageUrl;
      }
    });
  }

  openDeleteImageModal(idx: number) {
    const dialogData: CommonConfirmationDialog = {
      confirmMsg: 'Confirm Delete',
      confirmMsgDescription: 'Are you sure you want to delete this image?',
      buttons: [
        {
          name: 'No',
          class: '!text-base',
          color: 'accent',
        },
        {
          name: 'Yes, Delete',
          class: '!text-base',
          handler: () => {
            this.deleteFile(idx);
          },
        },
      ],
    };
    this.dialog.open(ConfirmDialogComponent, {
      maxWidth: '500px',
      width: '90%',
      panelClass: 'cmn-dialog',
      data: dialogData,
    });
  }

  deleteFile(idx: number) {
    const request: UploadMediaRequest = {
      recordId: this.data.recordId,
      moduleName: this.data.moduleName,
      referenceId: '0',
      referenceTable: [Constants.MediaType.ImageField, Constants.MediaType.MediaAns].includes(this.data.mediaSection)
                    ? Constants.MediaType.ImageField
                    : Constants.MediaType.Media,
      itemCode: this.data.itemInfo.itemCode,
      sectionNo: this.data.sectionNo,
      file: undefined,
      mediaKey: this.data.media[idx].data ?? '',
      action: UploadOperation.Delete,
      companyId: this.userPreferencesData.selectedCompanyId,
      uploadBy: this.userPreferencesData.userId,
      uploadedByName: this.userPreferencesData.userName
    };

    this.globalService
      .uploadMedia(request)
      .pipe(
        catchError(() => {
          this.toastService.showError('Failed to delete file.');
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response) {
          this.data.media.splice(idx, 1);
        }
      });
  }

  downloadFile(media: MediaFile): void {
    if (!media.url) {
      this.toastService.showError('File URL not available.');
      return;
    }
    try {
      const parsed = new URL(media.url);
      if (!['http:', 'https:'].includes(parsed.protocol)) return;
    } catch {
      return;
    }
    window.open(media.url, '_blank', 'noopener,noreferrer');
  }
}
